import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

// MRT
import {
    MaterialReactTable,
    MRT_ToggleDensePaddingButton,
    MRT_ToggleFiltersButton,
    MRT_ToggleFullScreenButton,
    MRT_ToggleGlobalFilterButton,
    useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';

// MUI
import {
    Box,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    Skeleton,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import StyleIcon from '@mui/icons-material/Style';
import { mkConfig, generateCsv, download } from 'export-to-csv';

const csvConfig = mkConfig({
    fieldSeparator: ',',
    decimalSeparator: '.',
    useKeysAsHeaders: true,
});

const normalize = (s) => String(s || '').trim().toLowerCase();

const getUnitPriceByKey = (product, key) => {
    const units = product?.pricing?.units;
    if (!Array.isArray(units)) return null;

    const name = (u) => normalize(u?.name);

    const matchers = {
        month: (n) => n.includes('month') || n.includes('monthly'),
        annually: (n) => n.includes('annual') || n.includes('annually') || n.includes('year'),
        triennial: (n) => n.includes('triennial') || n.includes('3 year') || n.includes('3-year'),
        license: (n) => n.includes('license') || n.includes('licence') || n.includes('perpetual') || n.includes('one-off'),
    };

    const hit = units.find((u) => matchers[key]?.(name(u)));
    if (!hit) return null;

    const v = hit.sellPrice;
    return typeof v === 'number' ? v : (v != null ? Number(v) : null);
};

const formatPrice = (value, currency) => {
    if (value == null || Number.isNaN(value)) return 'N/A';
    try {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: currency || 'EUR',
            maximumFractionDigits: 2,
        }).format(value);
    } catch {
        const num = typeof value === 'number' ? value : Number(value);
        return `${Number.isFinite(num) ? num.toFixed(2) : value} ${currency || ''}`.trim();
    }
};

const Mc3Products = () => {
    const [products, setProducts] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // prix: group (chips) ou columns (4 colonnes)
    const [priceView, setPriceView] = useState('group'); // 'group' | 'columns'

    // menu export
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    useEffect(() => {
        const run = async () => {
            try {
                setLoading(true);
                setError('');
                const { data } = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/mc3cloud/products`
                );
                setProducts(data?.data || []);
                setFiltered(data?.data || []);
            } catch (e) {
                setError(e?.response?.data?.error || e.message || 'Erreur');
            } finally {
                setLoading(false);
            }
        };
        run();
    }, []);

    // -------- CSV
    const csvData = filtered.map((p) => {
        const cur = p.pricing?.currency;
        return {
            id: p.id,
            name: p.name,
            code: p.code,
            status: p.deleted ? 'Supprimé' : 'Actif',
            currency: cur || 'N/A',
            priceMonth: formatPrice(getUnitPriceByKey(p, 'month'), cur),
            priceAnnually: formatPrice(getUnitPriceByKey(p, 'annually'), cur),
            priceTriennial: formatPrice(getUnitPriceByKey(p, 'triennial'), cur),
            priceLicense: formatPrice(getUnitPriceByKey(p, 'license'), cur),
            priceUnits: Array.isArray(p.pricing?.units)
                ? p.pricing.units.map((u) => `${u.name}:${u.sellPrice}`).join(' | ')
                : '',
            createdAt: p.createdAt ? new Date(p.createdAt).toLocaleDateString() : 'N/A',
            updatedAt: p.updatedAt ? new Date(p.updatedAt).toLocaleDateString() : 'N/A',
            pricingUpdatedAt: p.pricing?.updatedAt
                ? new Date(p.pricing.updatedAt).toLocaleDateString()
                : 'N/A',
        };
    });

    const handleExportRows = (rows) => {
        const rowData = rows.map((r) => r.original);
        const formatted = rowData.map((p) => {
            const cur = p.pricing?.currency;
            return {
                id: p.id,
                name: p.name,
                code: p.code,
                status: p.deleted ? 'Supprimé' : 'Actif',
                currency: cur || 'N/A',
                priceMonth: formatPrice(getUnitPriceByKey(p, 'month'), cur),
                priceAnnually: formatPrice(getUnitPriceByKey(p, 'annually'), cur),
                priceTriennial: formatPrice(getUnitPriceByKey(p, 'triennial'), cur),
                priceLicense: formatPrice(getUnitPriceByKey(p, 'license'), cur),
                priceUnits: Array.isArray(p.pricing?.units)
                    ? p.pricing.units.map((u) => `${u.name}:${u.sellPrice}`).join(' | ')
                    : '',
                createdAt: p.createdAt ? new Date(p.createdAt).toLocaleDateString() : 'N/A',
                updatedAt: p.updatedAt ? new Date(p.updatedAt).toLocaleDateString() : 'N/A',
                pricingUpdatedAt: p.pricing?.updatedAt
                    ? new Date(p.pricing.updatedAt).toLocaleDateString()
                    : 'N/A',
            };
        });

        const csv = generateCsv(csvConfig)(formatted);
        download(csvConfig)(csv);
    };

    const handleExportData = () => {
        const csv = generateCsv(csvConfig)(csvData);
        download(csvConfig)(csv);
    };

    // -------- Columns (prix groupé OU 4 colonnes)
    const columns = useMemo(() => {
        const baseCols = [
            {
                accessorKey: 'name',
                header: 'Produit',
                size: 320,
                filterVariant: 'autocomplete',
            },

            {
                accessorFn: (row) => row.pricing?.currency || 'N/A',
                id: 'currency',
                header: 'Devise',
                size: 70,
                Cell: ({ renderedCellValue }) =>
                    renderedCellValue && renderedCellValue !== 'N/A' ? (
                        <Chip label={renderedCellValue} size="small" variant="outlined" />
                    ) : (
                        <Chip label="N/A" size="small" variant="outlined" />
                    ),
                filterVariant: 'autocomplete',
            },
        ];

        const priceGroupColumn = {
            accessorFn: (row) => {
                const cur = row?.pricing?.currency;
                const month = getUnitPriceByKey(row, 'month');
                const annually = getUnitPriceByKey(row, 'annually');
                const triennial = getUnitPriceByKey(row, 'triennial');
                const license = getUnitPriceByKey(row, 'license');
                // renvoie une string pour pouvoir filtrer/autocomplete
                return [
                    month != null ? `Month:${formatPrice(month, cur)}` : null,
                    annually != null ? `Annually:${formatPrice(annually, cur)}` : null,
                    triennial != null ? `Triennial:${formatPrice(triennial, cur)}` : null,
                    license != null ? `License:${formatPrice(license, cur)}` : null,
                ]
                    .filter(Boolean)
                    .join(' | ');
            },
            id: 'prices',
            header: 'Prix',
            size: 360,
            Cell: ({ row }) => {
                const p = row.original;
                const cur = p.pricing?.currency;

                const entries = [
                    { key: 'month', label: 'Month' },
                    { key: 'annually', label: 'Annually' },
                    { key: 'triennial', label: 'Triennial' },
                    { key: 'license', label: 'License' },
                ]
                    .map((e) => ({ ...e, value: getUnitPriceByKey(p, e.key) }))
                    .filter((e) => e.value != null);

                if (!entries.length) return <Typography variant="caption">N/A</Typography>;

                return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {entries.map((e) => (
                            <Chip
                                key={e.key}
                                size="small"
                                variant="outlined"
                                label={`${e.label}: ${formatPrice(e.value, cur)}`}
                            />
                        ))}
                    </Box>
                );
            },
            filterVariant: 'autocomplete',
        };

        const priceColumns = [
            {
                accessorFn: (row) => getUnitPriceByKey(row, 'month'),
                id: 'priceMonth',
                header: 'Mensuel',
                size: 120,
                Cell: ({ row }) => {
                    const cur = row.original.pricing?.currency;
                    const val = getUnitPriceByKey(row.original, 'month');
                    return <Typography variant="body2">{formatPrice(val, cur)}</Typography>;
                },
                filterVariant: 'range',
            },
            {
                accessorFn: (row) => getUnitPriceByKey(row, 'annually'),
                id: 'priceAnnually',
                header: 'Annuel',
                size: 120,
                Cell: ({ row }) => {
                    const cur = row.original.pricing?.currency;
                    const val = getUnitPriceByKey(row.original, 'annually');
                    return <Typography variant="body2">{formatPrice(val, cur)}</Typography>;
                },
                filterVariant: 'range',
            },
            {
                accessorFn: (row) => getUnitPriceByKey(row, 'triennial'),
                id: 'priceTriennial',
                header: 'Triennal',
                size: 120,
                Cell: ({ row }) => {
                    const cur = row.original.pricing?.currency;
                    const val = getUnitPriceByKey(row.original, 'triennial');
                    return <Typography variant="body2">{formatPrice(val, cur)}</Typography>;
                },
                filterVariant: 'range',
            },
            {
                accessorFn: (row) => getUnitPriceByKey(row, 'license'),
                id: 'priceLicense',
                header: 'Licence',
                size: 130,
                Cell: ({ row }) => {
                    const cur = row.original.pricing?.currency;
                    const val = getUnitPriceByKey(row.original, 'license');
                    return <Typography variant="body2">{formatPrice(val, cur)}</Typography>;
                },
                filterVariant: 'range',
            },
        ];

        const unitChipsColumn = {
            accessorFn: (row) =>
                Array.isArray(row.pricing?.units) && row.pricing.units.length
                    ? row.pricing.units.map((u) => u.name).join(', ')
                    : 'N/A',
            id: 'priceUnits',
            header: 'Unités',
            size: 160,
            Cell: ({ row }) => {
                const units = row.original.pricing?.units || [];
                if (!units.length) return <Typography variant="caption">N/A</Typography>;
                return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {units.map((u) => (
                            <Chip key={u.id || u.name} label={u.name} size="small" variant="outlined" />
                        ))}
                    </Box>
                );
            },
            filterVariant: 'autocomplete',
        };

        const dateCols = [
            {
                accessorFn: (row) => (row.createdAt ? new Date(row.createdAt).toLocaleDateString() : 'N/A'),
                id: 'createdAt',
                header: 'Créé',
                size: 90,
            },
            {
                accessorFn: (row) => (row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : 'N/A'),
                id: 'updatedAt',
                header: 'MAJ',
                size: 90,
            },
        ];

        return [
            {
                id: 'product',
                columns: [
                    ...baseCols,
                    ...(priceView === 'columns' ? priceColumns : [priceGroupColumn]),
                    unitChipsColumn,
                    {
                        accessorFn: (row) => (row.deleted ? 'Supprimé' : 'Actif'),
                        id: 'status',
                        header: 'Statut',
                        size: 80,
                        Cell: ({ row }) =>
                            row.original.deleted ? (
                                <Chip label="Supprimé" color="error" size="small" />
                            ) : (
                                <Chip label="Actif" color="primary" size="small" />
                            ),
                        filterVariant: 'autocomplete',
                    },
                    ...dateCols,
                ],
            },
        ];
    }, [priceView]);

    const table = useMaterialReactTable({
    localization: MRT_Localization_FR,
    enableGlobalFilter: true,
    enableColumnFilters: true,

        columns,
        data: filtered || [],
        enableColumnFilterModes: true,
        enableColumnOrdering: true,
        enableGrouping: true,
        enableColumnPinning: true,
        enableFacetedValues: true,
        enableRowSelection: true,
        initialState: {
      showColumnFilters: true,
      showGlobalFilter: true,

            density: 'compact',
            columnPinning: {
                left: ['mrt-row-expand', 'mrt-row-select'],
                right: ['mrt-row-actions'],
            },
            pagination: { pageSize: 50 },
        },
        paginationDisplayMode: 'pages',
        positionToolbarAlertBanner: 'bottom',
        muiSearchTextFieldProps: { size: 'small', variant: 'outlined' },
        muiPaginationProps: {
            color: 'secondary',
            rowsPerPageOptions: [50, 100, 150],
            shape: 'rounded',
            variant: 'outlined',
        },

        renderDetailPanel: ({ row }) => {
            const pricing = row.original.pricing || {};
            const units = Array.isArray(pricing.units) ? pricing.units : [];

            return (
                <Box sx={{ p: 2 }}>

                    <Chip label={`Product Code: ${row.original.code || 'N/A'}`} size="small" />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Pricing
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        <Chip label={`freePeriod: ${pricing.freePeriod ? 'true' : 'false'}`} size="small" />
                        <Chip label={`currency: ${pricing.currency || 'N/A'}`} size="small" />
                        <Chip
                            label={`updated: ${pricing.updatedAt ? new Date(pricing.updatedAt).toLocaleString() : 'N/A'
                                }`}
                            size="small"
                            variant="outlined"
                        />
                    </Box>

                    <DataGrid
                        autoHeight
                        density="compact"
                        disableRowSelectionOnClick
                        rows={units.map((u, idx) => ({
                            id: u.id || idx,
                            name: u.name,
                            sellPrice: u.sellPrice,
                        }))}
                        columns={[
                            { field: 'name', headerName: 'Unité', flex: 1 },
                            { field: 'sellPrice', headerName: 'Prix', flex: 1 },
                        ]}
                        pageSizeOptions={[5, 10]}
                    />
                </Box>
            );
        },

        renderToolbarInternalActions: ({ table }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title="Vue prix">
                    <ToggleButtonGroup
                        size="small"
                        value={priceView}
                        exclusive
                        onChange={(_, v) => v && setPriceView(v)}
                    >
                        <ToggleButton value="group">
                            <StyleIcon fontSize="small" />
                        </ToggleButton>
                        <ToggleButton value="columns">
                            <ViewColumnIcon fontSize="small" />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Tooltip>

                <IconButton
                    id="export-button"
                    aria-controls={open ? 'export-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleClick}
                >
                    <FileDownloadIcon />
                </IconButton>

                <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                    <MenuItem
                        disabled={table.getRowModel().rows.length === 0}
                        onClick={() => handleExportRows(table.getRowModel().rows)}
                    >
                        Exporter la page actuelle
                    </MenuItem>

                    <MenuItem
                        disabled={table.getPrePaginationRowModel().rows.length === 0}
                        onClick={() => handleExportRows(table.getPrePaginationRowModel().rows)}
                    >
                        Exporter le filtre actuel
                    </MenuItem>

                    <MenuItem
                        disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                        onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
                    >
                        Exporter les lignes sélectionnées
                    </MenuItem>

                    <MenuItem disabled={filtered.length === 0} onClick={handleExportData}>
                        Exporter tout (filtré)
                    </MenuItem>
                </Menu>

                <MRT_ToggleDensePaddingButton table={table} />
                <MRT_ToggleFullScreenButton table={table} />
                <MRT_ToggleFiltersButton table={table} />
                <MRT_ToggleGlobalFilterButton table={table} />
            </Box>
        ),
    });

    return (
        <Box>
            <Typography variant="h6" my={1} mb={3}>
                Produits MC3 Cloud
            </Typography>

            {loading ? (
                <Skeleton variant="rectangular" width="100%" height={600} />
            ) : error ? (
                <Box sx={{ p: 2 }}>
                    <Typography color="error">{error}</Typography>
                </Box>
            ) : products.length === 0 ? (
                <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h6" color="textSecondary">
                        Aucune donnée disponible
                    </Typography>
                </Box>
            ) : (
                <MaterialReactTable table={table} />
            )}
        </Box>
    );
};

export default Mc3Products;
